import React, { useImperativeHandle, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHookToast, RCCServicesApi, RCCTooltip, arrayMove } from "react-common-components";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import SortableContainer from "src/components/Sortable/Container";
import SortableItem from "src/components/Sortable/Item";
import Title from "src/components/Title";
import Loader from "src/components/Loader";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { useAuth } from "src/context/Auth/context";
import { useLocation } from "react-router-dom";
import getUrlSearchParams from "src/utils/getUrlSearchParams";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import { restrictToVerticalAxis } from "src/components/Sortable/Container/modifiers";
import WrapText from "src/components/WrapText";
import { IMetba001002, IMetbe001, IRefContentStrategy, IRefModalGrouper, IRefModalItem } from "./interface";
import StrategyGrouper from "./components/strategyGrouper";
import ModalGrouper from "./components/modalGrouper";
import ModalItem from "./components/modalItem";



const ContentStrategy: React.ForwardRefRenderFunction<IRefContentStrategy> = (_, ref) => {

	const query = useLocation();
	const objQueryParams = getUrlSearchParams(query.search);

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { administrator, superUser } = useAuth();

	const [filters, setFilters] = useState<{objective: string, perspective: string}>({
		objective: objQueryParams?.objective ?? "",
		perspective: objQueryParams?.perspective ?? ""
	});

	const [selectedStrategy, setSelectedStrategy] = useState<number | null>(null);
	const [data, setData] = useState<IMetba001002 | null>(null);
	const [tempData, setTempData] = useState<IMetbe001[]| null>(null);
	const [loading, setIsLoading] = useState(true);
	const [waitingForResponse, setWaitingForResponse] = useState(false);

	const refModalStrategy = useRef<IRefModalGrouper>(null);
	const refModalitem = useRef<IRefModalItem>(null);

	const loadData = async (id: number) => {
		setSelectedStrategy(id);
		try{
			setIsLoading(true);
			const response = await RCCServicesApi.getRequest({
				method: "Strategies/SelectContent",
				urlParameters: {
					id,
					...filters 
				}
			});

			setData(response.data);
			setIsLoading(false);
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os dados"), addToast);
			setIsLoading(false);
		}
	};

	const queryReorderGroupers = async (groupers: any[]) => {
		setWaitingForResponse(true);
		try{
			await RCCServicesApi.putRequest({
				method: "Strategies/UpdateOrder",
				body: {
					rows: groupers
				}
			});
			setWaitingForResponse(false);
			
			addToast({
				color: "green",
				description: `${translate("Estratégia movida com sucesso")}`,
				title: translate("Sucesso")
			});

			return true;
		}catch(err){
			setData((prevState) => ({
				...prevState,
				metbe001: tempData
			} as IMetba001002));
			standardErrorMessages(err, `${translate("Erro ao mover estratégia")}`, addToast);
			setWaitingForResponse(false);
			return false;
		}
	};

	useImperativeHandle(ref, () => ({
		loadData
	}));

	const onCallbackChangeStrategy = (id: number, title: string) => {
		setData((prevState) => ({
			...prevState,
			metbe001: prevState?.metbe001.map((item) => {
				if(item.mee001Id === id){
					const itemChanged = item;
					itemChanged.mee001Titulo = title;
					return itemChanged;
				}
				return item;
			})
		} as IMetba001002));
	};

	const onCallbackAddStrategy = () => {
		if(selectedStrategy)
			loadData(selectedStrategy);
	};

	const onCallbackDeleteStrategy  = (id: number) => {
		setData((prevState) => ({
			...prevState,
			metbe001: prevState?.metbe001.filter((item) => item.mee001Id !== id)
		} as IMetba001002));
	};

	useEffectSkipInitialRender(() => {
		if(selectedStrategy)
			loadData(selectedStrategy);
	}, [filters]);

	return (<>
  
		<RCCGrid
			container 
			item
			justifyContent="space-between"
			xs={12}
		>
			<RCCGrid container item xs={10}>
				<Title color="purple" size="xl">
					<WrapText>
						{data?.mea001Nome}
					</WrapText>
				</Title>
			</RCCGrid>
			<RCCGrid container item xs={2} justifyContent="flex-end">
				{!loading && data && (
					<>
						<RCCTooltip
							position="bottom"
							text={translate("Mapas")}
							color="grey"
						>
							<RCCButton
								icon={{name: "map", color: "#FFF", size: 16}}
								color="grey"
								layout="isRounded"
								size="md"
								onClick={() => window.open(`/maps/panel/${data.mea001Id}`)}
							/>
						</RCCTooltip>
						<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
							{(data.mea008Permissao >= 2 || administrator || superUser) && (
								<RCCTooltip
									text={ `${translate("Nova Estratégia")}`}
									position="leftEnd"
									color="purple"
								>
									<RCCButton
										icon={{name: "plus", color: "#FFF", size: 18}}
										color="purple"
										layout="isRounded"
										onClick={() => {refModalStrategy.current?.actionButtonAdd(data.mea001Id);}}
										size="md"
									/>
								</RCCTooltip>
							)}
						</CheckPermission>
						{filters && (filters.objective !== "" || filters.perspective !== "") ? (
							<RCCTooltip
								text={translate("Limpar filtros")}
								color="yellow"
							>
								<RCCButton
									icon={{name: "broom", color: "#FFF", size: 16}}
									color="yellow"
									layout="isRounded"
									size="md"
									onClick={() => setFilters({ objective: "", perspective: "" })}
								/>
							</RCCTooltip>
						): null}	
					</>
				)}	

			</RCCGrid>
		</RCCGrid>
		{!loading && data && data.mea001Descricao && (
			<RCCGrid
				container
				item
				alignItems="center"
			>
				<Title color="grey" size="xs">
					{data.mea001Descricao}
				</Title>
			</RCCGrid>
		)}
		{loading ? (
			<Loader />
		) : (
			<>
				<RCCGrid styleInline="margin-top: 10px">
				 {!data || data.metbe001.length === 0 ? 
						(
							<RCCGrid 
								container 
								styleInline="margin: 10px"
							>
								<RCCAlert 
									title={translate("Atenção")}
									description={translate("Nenhuma estratégia encontrada")}
								/>
							</RCCGrid>
						) : (
							<>
								<SortableContainer
									onDragStart={() => {
										setTempData(data.metbe001);
									}}
  								onDragEnd={(activeIdX, overId) => {
										if(activeIdX === overId)
											return; 	

										const activeIndex = data.metbe001.findIndex((item) => item.id === activeIdX);
										const overIndex = data.metbe001.findIndex((item) => item.id === overId);

										const orderedItems = arrayMove(data.metbe001, activeIndex, overIndex);

										setData((prevState) => ({
											...prevState,
											metbe001: orderedItems
										} as IMetba001002));

										const rowsOrdered: any[] = [];

										orderedItems.forEach((item: any, index) => {
											const row = {
												mee001Id: item.mee001Id,
												mee001Ordem: index
											};
											rowsOrdered.push(row);
										});

										queryReorderGroupers(rowsOrdered);
									}}
									items={data.metbe001}
									modifiers={[restrictToVerticalAxis]}
  							>
								 {data?.metbe001.map((grouper) => (
										<div key={`container-strategies-${grouper.id}`}>
											<SortableItem 
												id={grouper.id} 
												key={grouper.id} 
												disabled={waitingForResponse}
											>
												<StrategyGrouper 
													refModalStrategy={refModalStrategy}
													refModalitem={refModalitem}
													strategyData={grouper}
													permissions={data.mea008Permissao}												
												/>
											</SortableItem>
										</div>
									))} 
								</SortableContainer>
							</>
						)
					} 
				</RCCGrid>
			</>
		)}


		<ModalGrouper 
			ref={refModalStrategy}
			onCallbackChange={onCallbackChangeStrategy}
			onCallbackAdd={onCallbackAddStrategy}
			onCallbackDelete={onCallbackDeleteStrategy}
		/>

		<ModalItem 
			ref={refModalitem}		
		/>
	</>);
};

export default React.forwardRef(ContentStrategy);
